import { useCallback } from 'react';
import { useToast } from '@/contexts/ToastContext';
import {
  useCreatePayNowOrderDeferred,
  useCompleteRegistrationPayment,
  useMintIntent,
  useRegistrationCredit,
} from '@/api/extensions/useRegistrationOps';
import { StripeCheckoutDialog } from './StripeCheckoutDialog';

interface PayNowDialogProps {
  open: boolean;
  onClose: () => void;
  registrationId: string;
  /** Display label for the event/registration (used in subtitle). */
  eventName?: string;
  /**
   * Optional pre-known total to surface in the dialog header before the
   * deferred quote arrives. Real total comes from totals_by_method on
   * the quote response.
   */
  fallbackTotal?: number | null;
  /**
   * Accepted for backwards compatibility with callers from the eager-mint
   * era. Ignored — the deferred-mint dialog reads fees from the quote's
   * totals_by_method and flips between card/ACH client-side.
   */
  transactionFeeTotal?: number | null;
  onSuccess?: () => void;
}

/**
 * Modal that drives the "Pay Now" flow for an existing pending_payment
 * registration. Uses the deferred-mint flow: the local payment row is
 * created via /pay-now-deferred/:id but no Stripe PI is minted until the
 * user clicks Pay. Verifies via /complete/:id once Stripe Elements
 * confirms. Thin wrapper around the shared StripeCheckoutDialog.
 */
export function PayNowDialog({
  open,
  onClose,
  registrationId,
  eventName,
  fallbackTotal,
  onSuccess,
}: PayNowDialogProps) {
  const { showSuccess } = useToast();
  const createPayNowDeferred = useCreatePayNowOrderDeferred();
  const completePayment = useCompleteRegistrationPayment();
  const mintIntent = useMintIntent();
  // Org credit the user can apply toward this entry (split tender). Only
  // fetched while the dialog is open.
  const { data: credit } = useRegistrationCredit(registrationId, open);

  const quoteOrder = useCallback(async () => {
    const res = await createPayNowDeferred.mutateAsync(registrationId);
    if (!res.payment_id) {
      throw new Error('Pay-now order is missing payment_id');
    }
    return {
      payment_id: res.payment_id,
      stripe_publishable_key: res.stripe_publishable_key,
      total_amount: res.total_amount,
      totals_by_method: res.totals_by_method,
    };
  }, [createPayNowDeferred, registrationId]);

  const confirmIntent = useCallback(
    async (
      paymentId: string,
      paymentMethodType: 'card' | 'us_bank_account',
      creditAmount?: number,
    ) => {
      return await mintIntent.mutateAsync({
        payment_id: paymentId,
        payment_method_type: paymentMethodType,
        credit_amount: creditAmount,
      });
    },
    [mintIntent],
  );

  const handleApprove = useCallback(
    async (
      paymentIntentId: string,
      methodInfo: { paymentMethodType: string | null; status: string },
    ) => {
      await completePayment.mutateAsync({
        registrationId,
        payment_intent_id: paymentIntentId,
      });
      const isAchProcessing =
        methodInfo.paymentMethodType === 'us_bank_account' ||
        methodInfo.status === 'processing';
      if (isAchProcessing) {
        showSuccess(
          'Entry confirmed!',
          'Bank transfer initiated — your spot is held while it clears (3-5 business days).',
        );
      } else {
        showSuccess('Payment successful!', 'Your entry is confirmed.');
      }
      onSuccess?.();
    },
    [completePayment, registrationId, showSuccess, onSuccess],
  );

  return (
    <StripeCheckoutDialog
      open={open}
      onClose={onClose}
      title="Pay entry fee"
      subtitle={eventName}
      amountLabel="Amount due"
      fallbackTotal={fallbackTotal}
      triggerKey={registrationId}
      quoteOrder={quoteOrder}
      confirmIntent={confirmIntent}
      availableCredit={credit?.available ?? 0}
      onApprove={handleApprove}
    />
  );
}
